/* --------------------------------------------------------------------------
   BEXLY — marquesina + ticker de reseñas.
   Reescrito por rendimiento: la versión anterior clonaba hasta 14 veces y
   llamaba a getBoundingClientRect dentro de bucles, forzando reflow una y otra
   vez (~2,9 s de hilo principal en un móvil medio).

   Ahora:
     · arranca en requestIdleCallback después de `load`, nunca en la ruta crítica
     · una sola fase de LECTURA, después una sola fase de ESCRITURA
     · clona lo justo (una vez) con un DocumentFragment
     · la animación es @keyframes CSS sobre transform (va a la GPU)
     · se pausa fuera de pantalla con IntersectionObserver
     · respeta prefers-reduced-motion
   Velocidades: marquesina 115 px/s · ticker 100 px/s
   -------------------------------------------------------------------------- */
(function () {
  'use strict';

  var MARQUEE_PX_S = 115;
  var TICKER_PX_S = 100;
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  var io = null;

  function observe(el) {
    if (!('IntersectionObserver' in window)) return;
    if (!io) {
      io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var host = entries[i].target;
          var off = !entries[i].isIntersecting;
          host.classList.toggle('bx-anim--paused', off);
          var inner = host.querySelectorAll('.bx-anim');
          for (var k = 0; k < inner.length; k++) inner[k].classList.toggle('bx-anim--paused', off);
        }
      }, { rootMargin: '150px' });
    }
    if (el.hasAttribute('data-bx-obs')) return;
    el.setAttribute('data-bx-obs', '');
    io.observe(el);
  }

  function arm(el, shift, pxPerSec, reverse) {
    if (!shift || shift < 20) return;
    el.style.setProperty('--bx-shift', '-' + Math.round(shift) + 'px');
    el.style.setProperty('--bx-dur', (shift / pxPerSec).toFixed(2) + 's');
    el.classList.add('bx-anim');
    if (reverse) el.classList.add('bx-anim--rev');
  }

  function build() {
    if (reduce && reduce.matches) return;

    /* ---------- FASE 1: solo lecturas ---------- */
    var jobs = [];

    var mcs = document.querySelectorAll('marquee-component');
    for (var i = 0; i < mcs.length; i++) {
      var mc = mcs[i];
      if (mc.hasAttribute('data-bx-done')) continue;
      var wrapper = mc.querySelector('.marquee__wrapper');
      var content = wrapper && wrapper.querySelector('.marquee__content');
      var items = content && content.querySelector('.marquee__repeated-items');
      if (!wrapper || !content || !items) continue;
      jobs.push({
        kind: 'marquee', root: mc, wrapper: wrapper, content: content, items: items,
        host: mc.getBoundingClientRect().width || window.innerWidth,
        itemW: items.getBoundingClientRect().width,
        gap: parseFloat(window.getComputedStyle(wrapper).columnGap) || 0
      });
    }

    var scrollers = document.querySelectorAll('[class*="testimonials-scrolling-"]');
    for (var s = 0; s < scrollers.length; s++) {
      var wraps = scrollers[s].querySelectorAll('[class*="testimonials-wrapper-"]');
      for (var w = 0; w < wraps.length; w++) {
        if (wraps[w].hasAttribute('data-bx-done')) continue;
        var lists = wraps[w].querySelectorAll('[class*="testimonials-list-"]');
        if (!lists.length) continue;
        jobs.push({
          kind: 'ticker', root: wraps[w], lists: lists, reverse: w % 2 === 1,
          listW: lists[0].getBoundingClientRect().width,
          host: window.innerWidth,
          gap: parseFloat(window.getComputedStyle(wraps[w]).columnGap) || 0
        });
      }
    }

    /* ---------- FASE 2: solo escrituras ---------- */
    for (var j = 0; j < jobs.length; j++) {
      var job = jobs[j];

      if (job.kind === 'marquee') {
        if (!job.itemW) continue;
        var copies = Math.max(1, Math.min(8, Math.ceil(job.host / job.itemW)));
        if (copies > 1) {
          var frag = document.createDocumentFragment();
          for (var c = 1; c < copies; c++) frag.appendChild(job.items.cloneNode(true));
          job.content.appendChild(frag);
        }
        // ancho calculado, no medido: evita un reflow por cada marquesina
        var contentW = job.itemW * copies + job.gap * (copies - 1);
        if (job.wrapper.children.length < 2) {
          var clone = job.content.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.removeAttribute('ref');
          job.wrapper.appendChild(clone);
        }
        arm(job.wrapper, contentW + job.gap, MARQUEE_PX_S, false);
        observe(job.root);
        job.root.setAttribute('data-bx-done', '');
      } else {
        /* El wrapper mide 200vw y arranca en left:-100vw, así que la banda de
           las dos listas tiene que llegar a 2 anchos de pantalla: si no, al
           terminar la vuelta el carrusel se queda vacío. Con 10 copias sobraba
           y no se notaba; con 2 hay que calcularlo. */
        var need = job.host * 2.2;
        var copies = Math.max(1, Math.min(6, Math.ceil(need / (job.listW || 1))));
        var bandW = job.listW;
        if (copies > 1) {
          for (var lc = 0; lc < job.lists.length; lc++) {
            var kids = [].slice.call(job.lists[lc].children);
            var tfrag = document.createDocumentFragment();
            for (var cc = 1; cc < copies; cc++) {
              for (var kk = 0; kk < kids.length; kk++) tfrag.appendChild(kids[kk].cloneNode(true));
            }
            job.lists[lc].appendChild(tfrag);
          }
          // ancho calculado, no medido: un reflow menos
          bandW = job.listW * copies + job.gap * (copies - 1);
        }
        for (var l = 0; l < job.lists.length; l++) {
          arm(job.lists[l], bandW + job.gap, TICKER_PX_S, job.reverse);
        }
        /* Se vigila el wrapper, no cada lista: una lista se saca a sí misma
           de la pantalla al desplazarse y, si se la observaba a ella, el
           navegador la pausaba fuera de cuadro y ya no volvía nunca.
           Con 10 copias la cinta era tan ancha que nunca salía del todo y el
           fallo no se veía; con 2 copias dejaba el carrusel vacío. */
        observe(job.root);
        job.root.setAttribute('data-bx-done', '');
      }
    }
  }

  function schedule() {
    if (window.requestIdleCallback) window.requestIdleCallback(build, { timeout: 2000 });
    else setTimeout(build, 200);
  }

  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule, { once: true });

  // el editor de temas recarga secciones sueltas
  document.addEventListener('shopify:section:load', function () { setTimeout(build, 150); });

  /* Solo se recalcula si cambia el ANCHO.
     En móvil, al arrastrar con el dedo, la barra de direcciones se encoge o
     se despliega y el navegador dispara `resize` con el mismo ancho y otro
     alto. Reconstruir ahí reiniciaba la animación desde cero: el carrusel
     daba un salto hacia atrás cada vez que tocabas la pantalla.
     La distancia del desplazamiento solo depende del ancho, así que un
     cambio de alto no obliga a recalcular nada. */
  var t;
  var lastWidth = window.innerWidth;
  function onViewportChange() {
    if (Math.abs(window.innerWidth - lastWidth) < 8) return;
    lastWidth = window.innerWidth;
    clearTimeout(t);
    t = setTimeout(function () {
      var done = document.querySelectorAll('[data-bx-done]');
      for (var i = 0; i < done.length; i++) done[i].removeAttribute('data-bx-done');
      var armed = document.querySelectorAll('.bx-anim');
      for (var k = 0; k < armed.length; k++) armed[k].classList.remove('bx-anim', 'bx-anim--rev', 'bx-anim--paused');
      var obs = document.querySelectorAll('[data-bx-obs]');
      for (var o = 0; o < obs.length; o++) {
        obs[o].removeAttribute('data-bx-obs');
        obs[o].classList.remove('bx-anim--paused');
        if (io) io.unobserve(obs[o]);
      }
      build();
    }, 300);
  }
  window.addEventListener('resize', onViewportChange);
  window.addEventListener('orientationchange', function () {
    setTimeout(onViewportChange, 350);
  });
})();

/* ======================================================================
   Carriles en móvil
   Pasos y segmentos: el carrusel lo hace el CSS; aquí solo se le da
      foco de teclado, etiqueta y contador, y se abre por la tarjeta que
      pida la URL (?scene=2 o ?segment=2).
   ====================================================================== */
(function () {
  'use strict';

  function rail(grid, cardSel, label, param) {
    if (!grid || grid.hasAttribute('data-bx-rail')) return;
    var cards = grid.querySelectorAll(cardSel);
    if (cards.length < 2) return;
    grid.setAttribute('data-bx-rail', '');
    grid.setAttribute('tabindex', '0');
    grid.setAttribute('role', 'group');
    grid.setAttribute('aria-label', label);

    var count = document.createElement('p');
    count.className = 'bx-rail-count';
    count.setAttribute('aria-live', 'polite');
    grid.insertAdjacentElement('afterend', count);

    function paint() {
      var mid = grid.scrollLeft + grid.clientWidth / 2;
      var n = 1;
      for (var i = 0; i < cards.length; i++) {
        if (cards[i].offsetLeft <= mid && cards[i].offsetLeft + cards[i].offsetWidth >= mid) { n = i + 1; break; }
      }
      count.textContent = n + ' / ' + cards.length;
    }
    var t;
    grid.addEventListener('scroll', function () { clearTimeout(t); t = setTimeout(paint, 60); }, { passive: true });
    paint();

    if (param) {
      var want = new URLSearchParams(location.search).get(param);
      var idx = parseInt(want, 10);
      if (idx >= 1 && idx <= cards.length) {
        grid.scrollLeft = cards[idx - 1].offsetLeft - (grid.clientWidth - cards[idx - 1].offsetWidth) / 2;
        paint();
      }
    }
  }

  function build() {
    if (window.matchMedia('(max-width: 749px)').matches) {
      rail(document.querySelector('.rln-steps__grid'), '.rln-steps__card', 'How it works, step by step', 'scene');
      rail(document.querySelector('.rln-segments__grid'), '.rln-segments__card', 'Which one is her', 'segment');
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', build);
  else build();
})();
